import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const tensorParallelComponent: ComponentDef = {
  id: 'tensor-parallel',
  name: 'Tensor Parallel',
  description: 'Shard individual weight matrices across GPUs: column-parallel splits output dim (no sync needed between layers), row-parallel splits input dim (AllReduce to merge).',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'tp_size', label: 'TP size', type: 'number', default: 4, min: 2, max: 16, step: 1 },
    {
      name: 'variant',
      label: 'Parallelism style',
      type: 'select',
      default: 'column',
      options: [
        { label: 'Column-parallel (split output dim)', value: 'column' },
        { label: 'Row-parallel (split input dim)', value: 'row' },
        { label: 'Megatron MLP (col→row)', value: 'megatron-mlp' },
        { label: 'Megatron Attention (col Q/K/V, row W_O)', value: 'megatron-attn' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const tp_size = Number(params.tp_size);
    const variant = String(params.variant || 'column');
    const d_local = Math.floor(d_model / tp_size);

    const g = new GraphBuilder();
    g.input('x', [d_model]);

    if (variant === 'column') {
      // Column-parallel: each GPU holds W[:,j:j+d_local] — output is a shard of d_local
      // No communication needed between column-parallel and following row-parallel layer
      g.linear('W_col_shard', 'x', [d_model, d_local]);
      g.activation('act', 'W_col_shard', 'activation  (per-GPU, no sync)');
      // AllGather to assemble full output (only if not followed by row-parallel)
      g.allgather('out_full', 'act', [d_model]);
      g.output('out', 'out_full');

    } else if (variant === 'row') {
      // Row-parallel: each GPU holds W[j:j+d_local,:] — requires partitioned input x_shard
      g.split('x_shard', 'x', [d_local], 'input shard');
      g.linear('W_row_shard', 'x_shard', [d_local, d_model]);
      // AllReduce: sum partial results from all GPUs to get full output
      g.allreduce('out', 'W_row_shard', [d_model]);
      g.output('out', 'out');

    } else if (variant === 'megatron-mlp') {
      // Megatron MLP: column-parallel W1 (no sync) → row-parallel W2 (AllReduce)
      g.linear('W1_col', 'x', [d_model, d_local]);          // each GPU: d_model → d_local
      g.activation('act', 'W1_col', 'GeLU  (no AllReduce between layers)');
      g.linear('W2_row', 'act', [d_local, d_model]);         // each GPU: d_local → d_model
      g.allreduce('out', 'W2_row', [d_model]);               // sum partial results
      g.output('out', 'out');

    } else {
      // Megatron Attention: Q/K/V column-parallel (each GPU: d_model → d_local heads)
      //                     W_O row-parallel (merge heads back)
      g.linear('Wq', 'x', [d_model, d_local]);
      g.linear('Wk', 'x', [d_model, d_local]);
      g.linear('Wv', 'x', [d_model, d_local]);
      // Local attention (on d_local-dim head partition)
      g.matmul('qk', 'Wq', 'Wk', [d_local, d_local]);
      g.softmax('attn', 'qk', -1);
      g.matmul('av', 'attn', 'Wv', [d_local, d_local]);
      // Row-parallel output projection + AllReduce
      g.linear('Wo_row', 'av', [d_local, d_model]);
      g.allreduce('out', 'Wo_row', [d_model]);
      g.output('out', 'out');
    }

    return g.build();
  }
};
